import { FC } from "react";
import { useAtomValue } from "jotai";
import { Chip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ortexColorState, textColorState } from "@/lib/store";
import { EventListRow, MixedEventStat } from "@/models/event-page";
import { eventStatus, formatPct } from "./helpers";
import styles from "./EventPageRevamp.module.scss";

interface NoTranscriptFillProps {
    event: EventListRow;
    /** All mixed-event stat rows of this event's type, for the summary tiles. */
    typeStats: MixedEventStat[] | null | undefined;
}

/**
 * Lighter "no transcript" fill for non-earnings events: a banner plus a few
 * summary tiles derived from this event type's historical next-day reactions.
 * Uses the shared CardComponent KPI tile treatment and an MUI Chip tag so it
 * matches the rest of the app.
 */
const NoTranscriptFill: FC<NoTranscriptFillProps> = ({ event, typeStats }) => {
    const accent = useAtomValue(ortexColorState);
    const textColor = useAtomValue(textColorState);
    const upcoming = eventStatus(event) !== "reported";

    const safeTypeStats = typeStats ?? [];
    const nextDayMoves = safeTypeStats.map((s) => s?.["1DayAfter"]).filter((v): v is number => v != null);
    const avgAbs = nextDayMoves.length ? nextDayMoves.reduce((a, v) => a + Math.abs(v), 0) / nextDayMoves.length : null;
    const largest = nextDayMoves.length ? nextDayMoves.reduce((a, v) => (Math.abs(v) > Math.abs(a) ? v : a), nextDayMoves[0]) : null;
    const upCount = nextDayMoves.filter((v) => v > 0).length;
    const upRate = nextDayMoves.length ? Math.round((upCount / nextDayMoves.length) * 100) : null;

    return (
        <section className={styles.ntxFill}>
            <div className={styles.ntxBanner}>
                <InfoOutlinedIcon className={styles.ntxBannerIcon} />
                <div>
                    <Chip size="small" variant="outlined" label={upcoming ? "Event not yet reported" : "No recorded call for this event"} sx={{ color: upcoming ? accent : textColor, borderColor: upcoming ? accent : textColor }} />
                    <p>
                        {upcoming
                            ? `This ${(event.type || "event").toLowerCase()} hasn't happened yet. Here's how the stock has historically behaved around this event type.`
                            : `This ${(event.type || "event").toLowerCase()} isn't a recorded call, so there's no transcript. Below is how the stock has historically reacted to events of this type.`}
                    </p>
                </div>
            </div>

            {safeTypeStats.length === 0 ? (
                <div className={styles.empty}>No historical statistics for this event type.</div>
            ) : (
                <div className={styles.ntxStats}>
                    <span className={styles.railLabel}>Historical reaction to {(event.type || "this event").toLowerCase()} events</span>
                    <div className={styles.ntxTiles}>
                        <div className={styles.metricTile}>
                            <span className={styles.metricLabel}>Avg next-day move</span>
                            <span className={styles.metricValue}>{avgAbs == null ? "—" : `±${avgAbs.toFixed(2)}%`}</span>
                        </div>
                        <div className={styles.metricTile}>
                            <span className={styles.metricLabel}>Largest move</span>
                            <span className={`${styles.metricValue} ${largest == null ? "" : largest >= 0 ? styles.up : styles.down}`}>{largest == null ? "—" : formatPct(largest)}</span>
                        </div>
                        <div className={styles.metricTile}>
                            <span className={styles.metricLabel}>Up rate</span>
                            <span className={styles.metricValue}>{upRate == null ? "—" : `${upRate}%`}</span>
                        </div>
                        <div className={styles.metricTile}>
                            <span className={styles.metricLabel}>Occurrences</span>
                            <span className={styles.metricValue}>{String(nextDayMoves.length)}</span>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default NoTranscriptFill;
